import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useLayoutEffect,
    useRef,
} from 'react';

import { type V3UiContextSnapshot } from '@apps/web/components/phoebe_chat/v3_ui_context';
import {
    selectV3UiContextSnapshot,
    type V3UiContextRegistration,
    type V3UiContextRegistrationInput,
} from '@apps/web/features/phoebe_chat_v3/model/ui_context';

type RegisterV3UiContext = (
    registration: V3UiContextRegistrationInput,
) => () => void;

const V3UiContextRegistry = createContext<RegisterV3UiContext | null>(null);

export function V3UiContextProvider({
    children,
    onSnapshotChange,
}: {
    readonly children: ReactNode;
    readonly onSnapshotChange: (snapshot: V3UiContextSnapshot | null) => void;
}) {
    const registrationsRef = useRef(new Map<string, V3UiContextRegistration>());
    const onSnapshotChangeRef = useRef(onSnapshotChange);
    onSnapshotChangeRef.current = onSnapshotChange;

    const publish = useCallback(() => {
        onSnapshotChangeRef.current(
            selectV3UiContextSnapshot([...registrationsRef.current.values()]),
        );
    }, []);
    const register = useCallback<RegisterV3UiContext>(
        (registration) => {
            registrationsRef.current.set(registration.key, registration);
            publish();
            return () => {
                if (
                    registrationsRef.current.get(registration.key) ===
                    registration
                ) {
                    registrationsRef.current.delete(registration.key);
                    publish();
                }
            };
        },
        [publish],
    );

    return (
        <V3UiContextRegistry.Provider value={register}>
            {children}
        </V3UiContextRegistry.Provider>
    );
}

export function useV3UiContextRegistration(
    registration: V3UiContextRegistrationInput | null,
): void {
    const register = useContext(V3UiContextRegistry);
    useLayoutEffect(() => {
        if (register === null || registration === null) {
            return undefined;
        }
        return register(registration);
    }, [register, registration]);
}
